import moment from 'moment';
import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { Button, Dropdown, Icon, Modal, Table } from 'semantic-ui-react';
import type { FlatPatient, FoodOrder, FoodOrderItem } from 'types';
import writeXlsxFile from 'write-excel-file';

import { AB_SCHEMA, FF_SCHEMA, MF_SCHEMA, UM_SCHEMA } from '../../vendorConfig';

/**
 * What the export control accepts. Single call site in `./index.tsx`, rendered
 * only when at least one order is selected and the dataset has loaded.
 *
 * `ordersData`, `selectedOrderIds`, `PATIENT_ATTRIBUTES` and `patients` are
 * dereferenced unguarded on the component's first statement, so they are
 * required: an absent one already throws today.
 */
type ExportOrdersProps = {
  /**
   * The ten flat-patient field names copied onto each exported row. Read as
   * `props.PATIENT_ATTRIBUTES.map(...)` with no guard.
   */
  PATIENT_ATTRIBUTES: string[];
  /**
   * Flips the exported orders to `status: 'exported'` after a successful write.
   * Required: both call sites in this file invoke it unguarded, and the only JSX
   * caller (`./index.tsx:1486`) always passes it.
   */
  markExported: () => void;
  /** Every order in the current dataset; filtered down by `selectedOrderIds`. */
  ordersData: FoodOrder[];
  /** Members keyed by id, used to decorate each row with delivery details. */
  patients: Record<string, FlatPatient> | null;
  /** Forwarded from the parent; not read by the export logic itself. */
  patientId?: string | null;
  /**
   * Ids ticked in the table. See the SUSPECTED DEFECT on `selectedOrderIds` in
   * `./index.tsx`: an id-less order here matches EVERY other id-less order below.
   */
  selectedOrderIds: (string | undefined)[];
  /** How many are ticked; drives the button label. */
  selectedOrdersLength?: number;
  /** A vendor code; selects the export schema and the EW language column. */
  vendor?: string;
};

/**
 * An order row decorated in place with the member's flat-patient attributes.
 *
 * The loop below copies each name in `PATIENT_ATTRIBUTES` off the matching
 * {@link FlatPatient} onto the order under that same key, so an exported row is
 * a `FoodOrder` plus an open set of member fields known only at runtime. The
 * `FoodOrder` half stays named so the schema accessors keep their checking.
 *
 * Values are `unknown` because `FlatPatient` types most of these `unknown[]`
 * (fhirpath); the loop takes `[0]` off the array ones and the string ones whole.
 */
type ExportedOrderRow = FoodOrder & Record<string, unknown>;

function ExportOrders(props: ExportOrdersProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  let exportData = props.ordersData
    .filter((o) => props.selectedOrderIds.includes(o.orderId))
    .map((element) => {
      /*
       * SUSPECTED DEFECT (the `value[0]` below) — REPORTED, NOT FIXED.
       * The `else` arm indexes `value` without checking it exists. `patient` is
       * a {@link FlatPatient} and `PATIENT_ATTRIBUTES` is a caller-supplied list
       * of field names, so an attribute the member has no value for reads
       * `undefined` and `undefined[0]` throws — taking down the whole export
       * rather than leaving one column blank. The `string` arm above it is the
       * only guarded case. Left as written; a `?.` here would silently export
       * incomplete rows on a path a care manager believes succeeded.
       */
      // The `{}` seed is dead — the next line overwrites it unconditionally —
      // but it is left as written; the annotation widens the binding to cover
      // both it and the assigned value without changing either.
      let patient: FlatPatient | Record<string, never> | null = {};
      patient = props.patients && props.patients[element.patientId!]!;
      const row = element as ExportedOrderRow;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
      patient &&
        props.PATIENT_ATTRIBUTES.map((attribute) => {
          const value = (patient as unknown as Record<string, unknown>)[attribute];
          if (typeof value === 'string') {
            row[attribute] = value;
          } else {
            row[attribute] = (value as unknown[])[0];
          }
        });
      //Eat well needs member language in invoices
      if (patient && props.vendor === 'EW') {
        row['language'] = patient['language']?.[0];
      }
      return row;
    });
  /*
   * One column of the workbook/CSV schema. The accessor takes an
   * {@link ExportedOrderRow} because half these columns read the member fields
   * the loop above wrote onto the order (`firstName`, `delivery_*`, `homePhone`,
   * `language`), which are not `FoodOrder` fields at all.
   */
  /*
   * `value` admits `undefined` because the four vendor-schema blocks below build
   * it through a `let v;` that is only assigned inside `if ('value' in element)`
   * / `else if ('attribute' in element)`. A schema entry carrying neither key
   * yields a column whose accessor is `undefined`, which `write-excel-file` then
   * calls — SUSPECTED DEFECT, reported not fixed. The four `*_SCHEMA` constants
   * in `vendorConfig` all satisfy one branch today, so it does not fire; the
   * union records that nothing enforces it.
   */
  // bundleName is orderName for orders created from table
  let schema: { column: string; value: ((order: ExportedOrderRow) => unknown) | undefined }[] = [
    { column: 'orderId', value: (order) => order.orderId },
    { column: 'orderDate', value: (order) => order.orderDate },
    { column: 'patientId', value: (order) => order.patientId },
    { column: 'bundleId', value: (order) => order.bundleId },
    { column: 'orderName', value: (order) => order.orderName },
    { column: 'bundleQuantity', value: (order) => order.bundleQuantity },
    { column: 'firstName', value: (order) => order.firstName },
    { column: 'lastName', value: (order) => order['lastName'] },
    { column: 'delivery_addressLine1', value: (order) => order['delivery_addressLine1'] },
    { column: 'delivery_addressLine2', value: (order) => order['delivery_addressLine2'] },
    { column: 'delivery_city', value: (order) => order['delivery_city'] },
    { column: 'delivery_state', value: (order) => order['delivery_state'] },
    { column: 'delivery_zip', value: (order) => order['delivery_zip'] },
    { column: 'homePhone', value: (order) => order['homePhone'] },
    { column: 'foodPrice', value: (order) => order?.foodPricing },
    { column: 'shippingFees', value: (order) => order?.shippingFees },
  ];
  if (props.vendor !== 'PK') {
    schema.push({ column: 'mobilePhone', value: (order) => order['mobilePhone'] });
  }

  if (props.vendor === 'OK') {
    schema.push({
      column: 'bundleItems',
      // `orderItems` is read unguarded here while `FoodOrder` declares it
      // optional — an OK-vendor order without line items throws mid-export.
      // Recorded with the assertion rather than guarded; see the note above.
      value: (order) => order.orderItems!.map((orderItem) => orderItem.name + '-' + orderItem.quantity).join(', '),
    });
  }
  if (props.vendor === 'EW') {
    schema.push({
      column: 'language',
      value: (order) => order['language'],
    });
  }
  schema.push({ column: 'deliveryInstructions', value: (order) => order.deliveryInstructions });

  if (props.vendor === 'AB') {
    schema = AB_SCHEMA.map((element) => {
      let v: ((order: ExportedOrderRow) => unknown) | undefined;
      if ('value' in element) {
        v = (_order) => element.value;
      } else if ('attribute' in element) {
        if (element.attribute.includes('+')) {
          // The branch is entered only when the attribute contains a '+', so the
          // split yields at least two parts; the assertions say that.
          const att = element.attribute.split('+');
          // Concatenation, not arithmetic: the operands coerce to string as upstream intends.
          // Wrapping them in String() would emit a call.
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          v = (order) => order[att[0]!] + ' ' + order[att[1]!];
        } else {
          v = (order) => order[element.attribute];
        }
      }
      return { column: element.column, value: v };
    });
  }
  if (props.vendor === 'MF') {
    schema = MF_SCHEMA.map((element) => {
      let v: ((order: ExportedOrderRow) => unknown) | undefined;
      if ('value' in element) {
        v = (_order) => element.value;
      } else if ('attribute' in element) {
        if (element.attribute === 'Unique ID') {
          v = (order) => 'MF_' + order['orderDate'];
        } else if (element.attribute === 'orderItems') {
          v = (order) => (order.orderItems ? order.orderItems.map((oi) => oi.name).join('\n') : '');
        } else {
          v = (order) => order[element.attribute];
        }
      }
      return { column: element.column, value: v };
    });
  }

  if (props.vendor === 'UM') {
    schema = UM_SCHEMA.map((element) => {
      let v: ((order: ExportedOrderRow) => unknown) | undefined;
      if ('attribute' in element) {
        if (element.column === 'Frequency_Unit') {
          v = (_order) => 'Single';
        } else if (element.column === 'Frequency_Qty') {
          v = (_order) => '1';
        } else if (element.column === 'Frequency_Repeat') {
          v = (_order) => 'Single';
        } else if (element.column === 'Preferred_DOW') {
          v = (_order) => '';
        } else if (element.column === 'Special Instructions') {
          v = (order) => order.deliveryInstructions;
        } else if (element.column === 'Program_ID') {
          v = (order) => order.patientOrg;
        } else if (element.column === 'Order_Date') {
          v = (order) => order.orderDate;
        } else if (element.column === 'SKU') {
          v = (order) => order.orderName;
        } else if (element.column === 'Phone') {
          v = (order) => order['mobilePhone'] || order['homePhone'];
        } else {
          v = (order) => order[element.attribute];
        }
      }
      return { column: element.column, value: v };
    });
  }

  if (props.vendor === 'FF') {
    const newExportData: Record<string, unknown>[] = [];
    /*
     * `for…in` over an array binds a STRING key, so both reads below index an
     * array with a `string` — legal at runtime, TS7015 to the checker. Typed by
     * declaring the loop variables as the element type rather than converting
     * the key, so nothing new runs; the `!` records that the loop only visits
     * keys the array has.
     *
     * `for…in` on an array also walks inherited enumerable keys, but changing it
     * to `for…of` is a behaviour change.
     */
    // for...in over an array also walks inherited enumerable keys; for...of would change what is
    // iterated.
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const orderIndex in exportData) {
      // Index-signature access on the array, not a method reference; the for...in key is a string.
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const order: ExportedOrderRow = exportData[orderIndex as keyof typeof exportData] as ExportedOrderRow;
      const items = order.orderItems;
      // for...in over an array also walks inherited enumerable keys; for...of would change what is
      // iterated.
      // eslint-disable-next-line @typescript-eslint/no-for-in-array
      for (const itemIndex in items) {
        // Index-signature access on the array, not a method reference; the for...in key is a
        // string.
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const item: FoodOrderItem = items[itemIndex as keyof typeof items] as FoodOrderItem;
        /*
         * SUSPECTED DEFECT (the `quantity` write below) — REPORTED, NOT FIXED.
         * This overwrites the ORDER's `quantity` (a `number`, the summed line-item
         * count the tables display) with the ITEM's `quantity` (a `string` off the
         * line item). For the FF vendor the exported `quantity` column therefore
         * carries a per-item count where every other vendor's carries the order
         * total, and the value changes type as well. `Record<string, unknown>`
         * rather than `ExportedOrderRow` here so the clashing write is expressible;
         * renaming the key is a change to what the vendor receives.
         */
        const temp: Record<string, unknown> = { ...order };
        temp['itemName'] = item['name'];
        temp['quantity'] = item['quantity'];
        newExportData.push(temp);
      }
    }
    exportData = [...newExportData];
    schema = FF_SCHEMA.map((element) => {
      let v: ((order: ExportedOrderRow) => unknown) | undefined;
      if ('value' in element) {
        v = (_order) => element.value;
      } else if ('attribute' in element) {
        if (element.attribute.toString().includes('+')) {
          // Concatenation, not arithmetic: the operands coerce to string as upstream intends.
          // Wrapping them in String() would emit a call.
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          v = (order) => order['delivery_addressLine1'] + ', ' + order['delivery_city'];
        } else {
          v = (order) => order[element.attribute];
        }
      }
      return { column: element.column, value: v };
    });
  }

  const handleExport = () => {
    // The four vendor branches above rebuild `schema` from attribute tables, so its
    // getters are typed `unknown` while the library declares them as its own
    // `ValueType`. Casting the callee rather than the argument keeps the shorthand.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget browser call; a rejection silently produces no file
    (writeXlsxFile as (data: ExportedOrderRow[], options: { schema: unknown; fileName: string }) => Promise<void>)(exportData, {
      schema,
      fileName: props.vendor + '-' + moment().utc().format().toString() + '.xlsx',
    });
    props.markExported();
    setConfirmOpen(false);
  };
  const csvButton = (
    <CSVLink
      style={{ color: 'black' }}
      onClick={() => {
        props.markExported();
        setConfirmOpen(false);
      }}
      filename={props.vendor + '-' + moment().utc().format().toString()}
      data={exportData && exportData}
      headers={[
        'orderId',
        'orderDate',
        'patientId',
        'bundleId',
        'orderName',
        'bundleQuantity',
        'firstName',
        'lastName',
        'delivery_addressLine1',
        'delivery_addressLine2',
        'delivery_city',
        'delivery_state',
        'delivery_zip',
        'homePhone',
        'mobilePhone',
        'deliveryInstructions',
      ]}
    >
      AS CSV
    </CSVLink>
  );
  const confirmText = '  All new orders will be marked exported';
  return (
    <>
      <Modal
        open={confirmOpen}
        onOpen={() => setConfirmOpen(true)}
        onClose={() => setConfirmOpen(false)}
        size="fullscreen"
        trigger={
          <Button
            color="green"
            icon="download"
            content={'(' + props.selectedOrdersLength + ')'}
            onClick={() => setConfirmOpen(true)}
          />
        }
      >
        <Modal.Header>
          <Icon name="warning" color="yellow" /> {confirmText}
        </Modal.Header>
        <Modal.Content scrolling>
          <Table celled singleLine>
            <Table.Header>
              <Table.Row>
                {/* <Table.HeaderCell>Order Id</Table.HeaderCell>
                            <Table.HeaderCell>Member Name</Table.HeaderCell>
                            <Table.HeaderCell>Order Name</Table.HeaderCell>
                            <Table.HeaderCell>Bundle Id</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell> */}
                {schema.map((sc) => {
                  return <Table.HeaderCell>{sc.column}</Table.HeaderCell>;
                })}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {/* {exportData.map(ed => {
                            return (
                                <Table.Row>
                                    <Table.Cell>{ed.orderId}</Table.Cell>
                                    <Table.Cell>{ed.firstName + ' ' + ed.lastName}</Table.Cell>
                                    <Table.Cell>{ed.orderName}</Table.Cell>
                                    <Table.Cell>{ed.bundleName}</Table.Cell>
                                    <Table.Cell>{ed.status.toUpperCase()}</Table.Cell>
                                </Table.Row>
                            )
                        })} */}
              {exportData.map((ed) => {
                return (
                  <Table.Row>
                    {schema.map((sc) => {
                      // Calls the accessor unguarded — the `| undefined` arm of
                      // `schema[].value` documented at its declaration lands here.
                      return <Table.Cell>{sc.value!(ed)}</Table.Cell>;
                    })}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          {/* <Button content='Yes, Export as Excel' onClick={handleExport} positive/> */}
          <Dropdown as={Button} upward color="green" text="Export">
            <Dropdown.Menu>
              {!['AB', 'MF', 'FF'].includes(props.vendor!) && <Dropdown.Item key="1">{csvButton}</Dropdown.Item>}
              <Dropdown.Item key="2" onClick={handleExport}>
                <a>AS EXCEL</a>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button content="Never Mind" onClick={() => setConfirmOpen(false)} negative />
        </Modal.Actions>
      </Modal>
    </>
  );
}

export default ExportOrders;
